import type { KickDto, ZoneDto } from '@penalty-cup/shared';
import { useEffect, useState } from 'react';

const ZONES: ZoneDto[] = ['TL', 'TC', 'TR', 'ML', 'MC', 'MR', 'BL', 'BC', 'BR'];
const ZONE_LABEL: Record<ZoneDto, string> = {
  TL: 'Верхний левый угол',
  TC: 'Под перекладину',
  TR: 'Верхний правый угол',
  ML: 'Слева по центру',
  MC: 'В центр',
  MR: 'Справа по центру',
  BL: 'Нижний левый угол',
  BC: 'Низом по центру',
  BR: 'Нижний правый угол',
};

// Ворота в координатах SVG 0..300 × 0..200: створ x 30..270, y 20..170, точка пенальти — внизу
const GOAL = { x0: 30, x1: 270, y0: 20, y1: 170 };
const col = (z: ZoneDto) => ({ L: 0, C: 1, R: 2 })[z[1] as 'L' | 'C' | 'R'];
const row = (z: ZoneDto) => ({ T: 0, M: 1, B: 2 })[z[0] as 'T' | 'M' | 'B'];
const cellW = (GOAL.x1 - GOAL.x0) / 3;
const cellH = (GOAL.y1 - GOAL.y0) / 3;
const zoneCenter = (z: ZoneDto) => ({
  x: GOAL.x0 + cellW * (col(z) + 0.5),
  y: GOAL.y0 + cellH * (row(z) + 0.5),
});

/** Куда летит мяч: в зону; мимо — за створ; в штангу — в каркас. */
function ballTarget(kick: KickDto) {
  const c = zoneCenter(kick.shotZone);
  if (kick.outcome === 'MISS') {
    const dx = col(kick.shotZone) === 0 ? -42 : col(kick.shotZone) === 2 ? 42 : 0;
    return { x: c.x + dx, y: row(kick.shotZone) === 0 || dx === 0 ? GOAL.y0 - 18 : c.y };
  }
  if (kick.outcome === 'POST') {
    if (row(kick.shotZone) === 0 && col(kick.shotZone) === 1) return { x: c.x, y: GOAL.y0 };
    return { x: col(kick.shotZone) === 0 ? GOAL.x0 : GOAL.x1, y: c.y };
  }
  return c;
}

const BALL_START = { x: 150, y: 192 };
const KEEPER_HOME = { x: 150, y: 128 };

interface Props {
  /** можно выбирать зону */
  selectable: boolean;
  selected?: ZoneDto | undefined;
  onSelect?: (zone: ZoneDto) => void;
  /** раскрытый удар — анимируем мяч и прыжок вратаря */
  kick?: KickDto | null;
  hint?: 'shoot' | 'keep' | null;
  /** цвет формы вратаря */
  keeperColor?: string | undefined;
  /** цвета мяча бьющего (магазин) */
  ballColors?: [string, string] | undefined;
}

export function Goal({ selectable, selected, onSelect, kick, hint, keeperColor, ballColors }: Props) {
  // Анимация стартует кадром позже, чтобы CSS-переход сработал от исходной точки
  const [played, setPlayed] = useState<number | null>(null);
  useEffect(() => {
    if (!kick) return;
    const id = requestAnimationFrame(() => setPlayed(kick.seq));
    return () => cancelAnimationFrame(id);
  }, [kick]);
  const animate = kick && played === kick.seq;
  const ball = animate ? ballTarget(kick) : BALL_START;
  // В верхний ряд вратарь тянется руками, а не вылетает над перекладиной
  const keeper = animate
    ? { x: zoneCenter(kick.keeperZone).x, y: Math.max(zoneCenter(kick.keeperZone).y, GOAL.y0 + 52) }
    : KEEPER_HOME;
  const lean = animate ? (col(kick.keeperZone) - 1) * 50 : 0;

  return (
    <div className={`goal ${hint ? `goal--${hint}` : ''}`}>
      <svg className="goal__svg" viewBox="0 0 300 200" aria-hidden="true">
        <defs>
          <pattern id="net" width="12" height="12" patternUnits="userSpaceOnUse">
            <path d="M0 0 L12 12 M12 0 L0 12" className="goal__net" />
          </pattern>
        </defs>
        <rect x="0" y="176" width="300" height="24" className="goal__grass" />
        <rect x={GOAL.x0} y={GOAL.y0} width={GOAL.x1 - GOAL.x0} height={GOAL.y1 - GOAL.y0} fill="url(#net)" />
        <path
          d={`M${GOAL.x0} ${GOAL.y1 + 6} V${GOAL.y0} H${GOAL.x1} V${GOAL.y1 + 6}`}
          className="goal__frame"
        />
        <line x1="0" y1={GOAL.y1 + 6} x2="300" y2={GOAL.y1 + 6} className="goal__line" />
        <g
          className="goal__keeper"
          style={{
            transform: `translate(${keeper.x - KEEPER_HOME.x}px, ${keeper.y - KEEPER_HOME.y}px) rotate(${lean}deg)`,
          }}
        >
          <circle cx={KEEPER_HOME.x} cy={KEEPER_HOME.y - 26} r="8" className="goal__keeper-head" />
          <rect
            x={KEEPER_HOME.x - 11}
            y={KEEPER_HOME.y - 17}
            width="22"
            height="28"
            rx="7"
            className="goal__keeper-body"
            style={keeperColor ? { fill: keeperColor } : undefined}
          />
          <path
            d={`M${KEEPER_HOME.x - 11} ${KEEPER_HOME.y - 12} l-14 -12 M${KEEPER_HOME.x + 11} ${KEEPER_HOME.y - 12} l14 -12`}
            className="goal__keeper-arms"
            style={keeperColor ? { stroke: keeperColor } : undefined}
          />
          <path
            d={`M${KEEPER_HOME.x - 6} ${KEEPER_HOME.y + 11} l-4 22 M${KEEPER_HOME.x + 6} ${KEEPER_HOME.y + 11} l4 22`}
            className="goal__keeper-legs"
          />
        </g>
        <g
          className={`goal__ball ${animate ? 'goal__ball--flying' : ''}`}
          style={{
            transform: `translate(${ball.x - BALL_START.x}px, ${ball.y - BALL_START.y}px) scale(${animate ? 0.7 : 1})`,
          }}
        >
          <circle
            cx={BALL_START.x}
            cy={BALL_START.y}
            r="8"
            className="goal__ball-shape"
            style={ballColors ? { fill: ballColors[0], stroke: ballColors[1] } : undefined}
          />
        </g>
      </svg>
      <div className="goal__zones" role="group" aria-label="Зоны ворот">
        {ZONES.map((z) => (
          <button
            key={z}
            type="button"
            className={`goal__zone ${selected === z ? 'goal__zone--selected' : ''}`}
            aria-label={ZONE_LABEL[z]}
            aria-pressed={selected === z}
            disabled={!selectable}
            onClick={() => onSelect?.(z)}
          />
        ))}
      </div>
    </div>
  );
}
